import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { FileText, Download, RefreshCw } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useBusinesses } from "@/hooks/use-businesses";
import { Sidebar } from "@/components/ui/sidebar";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const calculateDateRange = (period: string) => {
  const now = new Date();
  let startDate: string, endDate: string;

  switch (period) {
    case "this-month":
      startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      endDate = now.toISOString().split('T')[0];
      break;
    case "last-month":
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      startDate = lastMonth.toISOString().split('T')[0];
      endDate = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
      break;
    case "this-quarter":
      const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
      startDate = quarterStart.toISOString().split('T')[0];
      endDate = now.toISOString().split('T')[0];
      break;
    case "last-quarter":
      const lastQuarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 - 3, 1);
      const lastQuarterEnd = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 0);
      startDate = lastQuarterStart.toISOString().split('T')[0];
      endDate = lastQuarterEnd.toISOString().split('T')[0];
      break;
    case "this-year":
      startDate = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
      endDate = now.toISOString().split('T')[0];
      break;
    case "last-year":
      startDate = new Date(now.getFullYear() - 1, 0, 1).toISOString().split('T')[0];
      endDate = new Date(now.getFullYear() - 1, 11, 31).toISOString().split('T')[0];
      break;
    default: // "all-dates"
      startDate = "2020-01-01";
      endDate = "2030-12-31";
      break;
  }

  return { startDate, endDate };
};

interface ProfitLossCategory {
  category: string;
  transactionCount: number;
  amount: number;
}

interface ProfitLossData {
  revenue: string | number;
  expenses: string | number;
  totalExpenses?: number;
  netIncome: number;
  expenseCategories: ProfitLossCategory[];
}

export default function ProfitLossReport() {
  const { activeBusiness } = useBusinesses();
  const [selectedPeriod, setSelectedPeriod] = useState("all-dates");
  const [loading, setLoading] = useState(false);

  const { startDate, endDate } = calculateDateRange(selectedPeriod);

  const { data: reportData, refetch } = useQuery<ProfitLossData>({
    queryKey: ['/api/businesses', activeBusiness?.id, 'reports/profit-loss', selectedPeriod, startDate, endDate],
    queryFn: () => 
      fetch(`/api/businesses/${activeBusiness?.id}/reports/profit-loss?startDate=${startDate}&endDate=${endDate}&t=${Date.now()}`)
        .then(res => res.json()),
    enabled: !!activeBusiness?.id,
    staleTime: 0,
    gcTime: 0,
  });

  const fetchReportData = async () => {
    setLoading(true);
    try {
      await refetch();
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
  };

  const exportReport = (format: string) => {
    if (format === 'pdf') {
      exportToPDF();
    }
  };

  const exportToPDF = () => {
    if (!reportData || !activeBusiness) return;

    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(activeBusiness.name, 20, 25);
    
    doc.setFontSize(16);
    doc.text('Profit & Loss Statement', 20, 35);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const periodText = selectedPeriod.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
    doc.text(`Period: ${periodText} | Generated: ${new Date().toLocaleDateString()}`, 20, 45);
    
    // Horizontal line
    doc.setLineWidth(0.5);
    doc.line(20, 50, 190, 50);
    
    let yPos = 60;
    
    // Revenue Section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 128, 0);
    doc.text('REVENUE', 20, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text('Sales Revenue', 25, yPos);
    doc.text(`$${Number(reportData.revenue).toLocaleString()}`, 150, yPos);
    yPos += 8;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Total Revenue', 25, yPos);
    doc.text(`$${Number(reportData.revenue).toLocaleString()}`, 150, yPos);
    yPos += 15;
    
    // Expenses Section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(128, 0, 0);
    doc.text('EXPENSES', 20, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    
    // Show expense categories
    if (reportData.expenseCategories && reportData.expenseCategories.length > 0) {
      reportData.expenseCategories.forEach((category) => {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
          // Add header on new page
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(128, 0, 0);
          doc.text('EXPENSES (continued)', 20, yPos);
          yPos += 8;
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(0, 0, 0);
        }
        doc.text(`${category.category} (${category.transactionCount} transactions)`, 25, yPos);
        doc.text(`$${category.amount.toLocaleString()}`, 150, yPos);
        yPos += 6;
      });
    }
    
    yPos += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Total Expenses', 25, yPos);
    doc.text(`$${Number(reportData.expenses).toLocaleString()}`, 150, yPos);
    yPos += 15;
    
    // Net Income
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.line(20, yPos, 180, yPos);
    yPos += 8;
    doc.text('NET INCOME', 20, yPos);
    doc.text(`$${reportData.netIncome.toLocaleString()}`, 150, yPos);
    
    // Save the PDF
    doc.save(`profit-loss-${activeBusiness.name}-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  if (!activeBusiness) {
    return (
      <div className="p-6">
        <p>Please select a business to view reports.</p>
      </div>
    );
  }

  const revenue = Number(reportData?.revenue ?? 0);
  const expenses = Number(reportData?.expenses ?? reportData?.totalExpenses ?? 0);
  const netIncome = Number(reportData?.netIncome ?? 0);

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 ml-64 p-6 space-y-6">
      {/* Header Section - Hidden in print */}
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h1 className="text-3xl font-bold">Profit & Loss Statement</h1>
          <p className="text-muted-foreground">Financial performance for {activeBusiness.name}</p>
        </div>
        
        <div className="flex items-center gap-4">
          <Button onClick={() => exportReport('pdf')} variant="outline">
            <FileText className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          
          <Button onClick={() => exportReport('excel')} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          
          <Button onClick={fetchReportData} disabled={loading}>
            <RefreshCw className="w-4 h-4 mr-2" />
            {loading ? "Loading..." : "Refresh Data"}
          </Button>
        </div>
      </div>

      {/* Period Selector Card - Hidden in print */}
      <Card className="print:hidden">
        <CardHeader>
          <CardTitle>Report Period</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-center">
            <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-dates">All Dates</SelectItem>
                <SelectItem value="this-week">This Week</SelectItem>
                <SelectItem value="last-week">Last Week</SelectItem>
                <SelectItem value="this-month">This Month</SelectItem>
                <SelectItem value="last-month">Last Month</SelectItem>
                <SelectItem value="this-quarter">This Quarter</SelectItem>
                <SelectItem value="last-quarter">Last Quarter</SelectItem>
                <SelectItem value="this-year">This Year</SelectItem>
                <SelectItem value="last-year">Last Year</SelectItem>
                <SelectItem value="custom">Custom Dates</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={fetchReportData} disabled={loading} variant="outline">
              {loading ? "Updating..." : "Update Report"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Clean PDF Header - Only shown in print */}
      <div className="hidden print:block text-center mb-8">
        <h1 className="text-2xl font-bold mb-2">{activeBusiness.name}</h1>
        <h2 className="text-xl mb-4">Profit & Loss Statement</h2>
        <p className="text-sm text-gray-600">
          Period: {selectedPeriod.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())} | 
          Generated: {new Date().toLocaleDateString()}
        </p>
      </div>

      {/* Profit & Loss Statement */}
      <Card>
        <CardHeader>
          <CardTitle>Profit & Loss Statement</CardTitle>
          <p className="text-sm text-muted-foreground">For {activeBusiness.name}</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Revenue Section */}
            <div className="border-b pb-4">
              <h3 className="font-semibold text-green-700 mb-3">Revenue</h3>
              <div className="space-y-2 ml-4">
                <div className="flex justify-between">
                  <span>Sales Revenue</span>
                  <span className="font-medium">${revenue.toLocaleString()}</span>
                </div>
              </div>
              <div className="flex justify-between font-semibold mt-2 pt-2 border-t">
                <span>Total Revenue</span>
                <span className="text-green-600">${revenue.toLocaleString()}</span>
              </div>
            </div>

            {/* Expenses Section */}
            <div className="border-b pb-4">
              <h3 className="font-semibold text-red-700 mb-3">Expenses</h3>
              <div className="space-y-2 ml-4">
                {reportData?.expenseCategories && reportData.expenseCategories.length > 0 ? (
                  reportData.expenseCategories.map((category, index) => (
                    <div key={index} className="flex justify-between">
                      <span className="flex items-center">
                        {category.category}
                        <span className="text-xs text-gray-500 ml-2">
                          ({category.transactionCount} transactions)
                        </span>
                      </span>
                      <span className="font-medium">${category.amount.toLocaleString()}</span>
                    </div>
                  ))
                ) : (
                  <div className="flex justify-between">
                    <span>Operating Expenses</span>
                    <span className="font-medium">${expenses.toLocaleString()}</span>
                  </div>
                )}
              </div>
              <div className="flex justify-between font-semibold mt-2 pt-2 border-t">
                <span>Total Expenses</span>
                <span className="text-red-600">${expenses.toLocaleString()}</span>
              </div>
            </div>

            {/* Net Income */}
            <div className="pt-4">
              <div className="flex justify-between text-lg font-bold">
                <span>Net Income</span>
                <span className={netIncome >= 0 ? "text-green-600" : "text-red-600"}>
                  ${netIncome.toLocaleString()}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Revenue minus total expenses
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
